import type { Translations } from '../types';

export const zh: Translations = {
  common: {
    home: '首页',
    github: 'GitHub',
    copy: '复制',
    clear: '清空',
    input: '输入',
    output: '输出',
    encode: '编码',
    decode: '解码',
    generate: '生成',
    convert: '转换',
    result: '结果',
    copiedToClipboard: '已复制到剪贴板!',
    failedToCopy: '复制失败',
    language: '语言',
  },
  seo: {
    siteDescription: '免费、注重隐私的开发者工具集合。JSON格式化、Base64编解码、UUID生成、哈希生成、URL编码、时间戳转换和颜色转换。所有工具都可在浏览器中离线使用。',
    keywords: [
      '开发者工具',
      'JSON格式化',
      'Base64编码',
      'UUID生成器',
      '哈希生成器',
      'URL编码',
      '时间戳转换',
      '颜色转换',
      '在线工具',
      '免费工具',
      '隐私保护',
      '离线工具',
    ],
    ogTitle: 'GetDev.Tools - 免费注重隐私的开发者工具',
    ogDescription: '免费开发者工具集合，可在浏览器中离线使用。包括JSON格式化、Base64、UUID、哈希、URL编码等工具。',
  },
  home: {
    title: '开发者工具集合',
    subtitle: '免费、注重隐私的开发者工具。所有处理都在您的浏览器中进行。',
    whyTitle: '为什么选择 GetDev.Tools?',
    privacyTitle: '🔒 隐私优先',
    privacyDesc: '所有处理都在您的浏览器中进行。您的数据永远不会离开您的设备。',
    fastTitle: '⚡ 闪电般快速',
    fastDesc: '无需服务器往返。所有操作都在本地运行，立即获得结果。',
    freeTitle: '🆓 永久免费',
    freeDesc: '开源且免费使用。无需注册或付费。',
    footer: '© 2025 GetDev.Tools - 注重隐私的开发者工具',
    footerSubtitle: '所有处理都在您的浏览器中进行',
  },
  tools: {
    jsonFormatter: {
      name: 'JSON 格式化',
      description: '格式化、验证和美化 JSON 数据',
      format: '格式化',
      minify: '压缩',
      validate: '验证',
      error: '错误',
    },
    base64: {
      name: 'Base64 编解码',
      description: '编码和解码 Base64 字符串',
      encodeButton: '编码',
      decodeButton: '解码',
    },
    uuid: {
      name: 'UUID 生成器',
      description: '生成符合 RFC 4122 的 UUID v4 标识符',
      numberOfUuids: '要生成的 UUID 数量',
      generatedUuids: '已生成的 UUID',
      copyAll: '全部复制',
      aboutTitle: '关于 UUID v4',
      aboutDesc: 'UUID（通用唯一标识符）v4 是随机生成的 128 位标识符。碰撞概率极低，适用于无需中央协调的分布式系统。',
    },
    hash: {
      name: '哈希生成器',
      description: '生成 SHA-1、SHA-256、SHA-512 哈希值',
      inputText: '输入文本',
      securityNotice: '⚠️ 安全提示',
      securityDesc: 'SHA-1 已因安全原因被弃用。请使用 SHA-256 或 SHA-512 进行加密应用。由于安全漏洞，现代浏览器中不提供 MD5。',
    },
    urlEncoder: {
      name: 'URL 编解码',
      description: '编码和解码 URL 字符串',
      encodeUrl: '编码 URL',
      encodeComponent: '编码组件',
      encoded: '已编码',
      decoded: '已解码',
      differenceTitle: '💡 区别说明',
      encodeUriDesc: 'encodeURI: 编码完整 URI（不编码: ; , / ? : @ & = + $ #）',
      encodeComponentDesc: 'encodeURIComponent: 编码 URI 组件（编码所有特殊字符）',
      examples: '示例',
    },
    timestamp: {
      name: '时间戳转换',
      description: '在 Unix 时间戳和可读日期之间转换',
      currentTime: '当前时间',
      unixMs: 'Unix 时间戳（毫秒）',
      unixS: 'Unix 时间戳（秒）',
      toDate: '时间戳 → 日期',
      toTimestamp: '日期 → 时间戳',
      useNow: '使用当前时间',
      aboutTitle: '💡 关于 Unix 时间戳',
      aboutDesc1: '• Unix 时间戳 = 自 1970 年 1 月 1 日 00:00:00 UTC（纪元）以来的秒数',
      aboutDesc2: '• 10 位数字 = 秒（例如，1609459200）',
      aboutDesc3: '• 13 位数字 = 毫秒（例如，1609459200000）',
      aboutDesc4: '• 支持的日期格式：ISO 8601、RFC 2822 或自然语言',
    },
    color: {
      name: '颜色转换',
      description: '在 HEX、RGB 和 HSL 格式之间转换',
      pickColor: '选择颜色',
      formatGuide: '💡 颜色格式指南',
      hexDesc: 'HEX: 十六进制表示法 (#RRGGBB)',
      rgbDesc: 'RGB: 红、绿、蓝值 (0-255)',
      hslDesc: 'HSL: 色相 (0-360°)、饱和度 (0-100%)、亮度 (0-100%)',
      alphaDesc: 'RGBA/HSLA: 与 RGB/HSL 相同，带有 Alpha（不透明度）通道',
    },
    jwt: {
      name: 'JWT 解码器',
      description: '解码和检查 JSON Web Token',
      inputToken: 'JWT 令牌',
      header: '头部',
      payload: '负载',
      signature: '签名 (Base64)',
      claims: '标准声明',
      invalidFormat: 'JWT 格式无效。令牌必须由点分隔的 3 部分组成。',
      invalidToken: 'JWT 令牌无效',
      securityNotice: '安全提示',
      securityDesc: '此工具仅解码 JWT 令牌，不验证签名。请勿粘贴来自生产系统的敏感令牌。',
      aboutTitle: '💡 关于 JWT',
      aboutDesc1: 'JWT = JSON Web Token，用于安全身份验证',
      aboutDesc2: '格式：头部.负载.签名（Base64 编码）',
      aboutDesc3: '头部包含算法和令牌类型，负载包含声明',
    },
    diff: {
      name: '文本比较',
      description: '比较两段文本并查看差异',
      originalText: '原始文本',
      modifiedText: '修改后文本',
      sideBySide: '并排显示',
      unified: '统一显示',
      noDifference: '未发现差异',
    },
    markdown: {
      name: 'Markdown 预览',
      description: '实时编写和预览 Markdown',
      editor: '编辑器',
      preview: '预览',
      supportedFeatures: '支持：标题、列表、链接、图片、代码块、表格等',
    },
    regex: {
      name: '正则表达式测试',
      description: '测试正则表达式并查看匹配结果',
      pattern: '正则表达式',
      flags: '标志',
      testString: '测试字符串',
      matches: '匹配结果',
      noMatches: '未找到匹配',
      invalidRegex: '正则表达式无效',
      globalFlag: '全局 (g)',
      caseInsensitive: '不区分大小写 (i)',
      multiline: '多行 (m)',
      dotAll: '点匹配换行符 (s)',
    },
    qrCode: {
      name: '二维码生成器',
      description: '生成网址、文本、WiFi 等二维码',
      inputData: '输入数据',
      qrCodeImage: '二维码图片',
      size: '尺寸 (像素)',
      errorCorrection: '容错率',
      errorLow: '低 (~7%)',
      errorMedium: '中 (~15%)',
      errorQuartile: '高 (~25%)',
      errorHigh: '最高 (~30%)',
      templates: '快速模板',
      templateUrl: '网站网址',
      templateEmail: '邮箱',
      templatePhone: '电话号码',
      templateSms: '短信',
      templateWifi: 'WiFi 网络',
      templateVcard: '联系人卡片 (vCard)',
      privacyNotice: '🔒 隐私说明',
      privacyDesc: '二维码使用外部 API (qrserver.com) 生成。您的数据将被发送到他们的服务器。',
    },
    lorem: {
      name: 'Lorem Ipsum 生成器',
      description: '为设计和原型生成占位文本',
      generateType: '生成类型',
      paragraphs: '段落',
      sentences: '句子',
      words: '单词',
      count: '数量',
      startWithLorem: '以 "Lorem ipsum" 开头',
      generatedText: '生成的文本',
      aboutTitle: '💡 关于 Lorem Ipsum',
      aboutDesc: 'Lorem Ipsum 是设计和出版中常用的占位文本。它近似于英文的字母频率，看起来像可读的文本。',
    },
    htmlEntity: {
      name: 'HTML 实体编码器',
      description: '编码和解码 HTML 实体',
      inputText: '输入文本',
      encodeBasic: '编码（基本）',
      encodeAll: '全部编码',
      decode: '解码',
      commonEntities: '常用 HTML 实体',
      character: '字符',
      entity: '实体',
      description2: '说明',
      useCases: '💡 使用场景',
      useCase1: '基本编码：仅编码关键字符 (<, >, &, ", \')',
      useCase2: '全部编码：编码所有特殊字符以获得最大安全性',
      useCase3: '解码：将 HTML 实体转换回普通字符',
    },
  },
};

